import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import {
  Plus,
  Pencil,
  Trash2,
  X,
  HelpCircle,
  RefreshCw,
  CheckSquare,
  Square,
  Search,
  Loader2,
  Download,
  Upload,
  AlertCircle,
  CheckCircle,
} from 'lucide-react'
import PhoneInput from '../../components/PhoneInput'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import { getApiErrorKey } from '../../utils/apiError'
import type { AdminCustomer, AdminERP } from '../../types'
import { exportToCSV, parseCSV } from '../../utils/csv'

interface CustomerFormData {
  erp_id: number | ''
  erp_customer_id: string
  customer_name: string
  siren: string
  email: string
  phone: string
  address: string
  city: string
  postal_code: string
  is_active: boolean
}

const emptyForm: CustomerFormData = {
  erp_id: '',
  erp_customer_id: '',
  customer_name: '',
  siren: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  postal_code: '',
  is_active: true,
}

interface SyncCandidate {
  erp_customer_id: string
  customer_name: string
  city: string | null
  email: string | null
}

export default function AdminCustomers() {
  const { t } = useTranslation()
  const [customers, setCustomers] = useState<AdminCustomer[]>([])
  const [erps, setErps] = useState<AdminERP[]>([])
  const [customerIdHelps, setCustomerIdHelps] = useState<
    Record<string, { placeholder: string; help: string }>
  >({})
  const defaultHelp = { placeholder: '', help: '' }

  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<CustomerFormData>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [filterErpId, setFilterErpId] = useState<number | ''>('')

  // Sync state
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [syncErpId, setSyncErpId] = useState<number | ''>('')
  const [syncLimit, setSyncLimit] = useState(10)
  const [syncCandidates, setSyncCandidates] = useState<SyncCandidate[]>([])
  const [syncTotalInErp, setSyncTotalInErp] = useState(0)
  const [syncAlreadySynced, setSyncAlreadySynced] = useState(0)
  const [syncSelected, setSyncSelected] = useState<Set<string>>(new Set())
  const [syncLoading, setSyncLoading] = useState(false)
  const [syncImporting, setSyncImporting] = useState(false)
  const [syncResult, setSyncResult] = useState<{ imported: number; skipped: number } | null>(null)
  const [syncError, setSyncError] = useState('')

  // ERP name search (create modal autocomplete)
  const [erpSearchQuery, setErpSearchQuery] = useState('')
  const [erpSearchResults, setErpSearchResults] = useState<
    Array<{ erp_customer_id: string; customer_name: string; city: string | null }>
  >([])
  const [erpSearchLoading, setErpSearchLoading] = useState(false)
  const [showErpDropdown, setShowErpDropdown] = useState(false)
  const erpSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // CSV import state
  const [showImportModal, setShowImportModal] = useState(false)
  const [importErpId, setImportErpId] = useState<number | ''>('')
  const [importRows, setImportRows] = useState<Record<string, string>[]>([])
  const [importFileName, setImportFileName] = useState('')
  const [importLoading, setImportLoading] = useState(false)
  const [importResult, setImportResult] = useState<{
    created: number
    updated: number
    errors: { line: number; message: string }[]
  } | null>(null)
  const [importError, setImportError] = useState('')
  const importFileRef = useRef<HTMLInputElement>(null)

  // Sync activity filters
  const [syncFilterInvoices, setSyncFilterInvoices] = useState(false)
  const [syncFilterInvoicesMonths, setSyncFilterInvoicesMonths] = useState(6)
  const [syncFilterProposals, setSyncFilterProposals] = useState(false)
  const [syncFilterProposalsMonths, setSyncFilterProposalsMonths] = useState(6)

  const loadData = useCallback(async () => {
    try {
      const [customersData, erpsData, helpsData] = await Promise.all([
        api.getAdminCustomers(),
        api.getAdminERPs(),
        api.getCustomerIdHelps(),
      ])
      setCustomers(customersData)
      setErps(erpsData)
      setCustomerIdHelps(helpsData)
    } catch {
      setError(t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [t])

  const loadCustomers = useCallback(async () => {
    const filters = filterErpId ? { erp_id: filterErpId as number } : undefined
    const data = await api.getAdminCustomers(filters)
    setCustomers(data)
  }, [filterErpId])

  useEffect(() => {
    loadData()
  }, [loadData])
  useEffect(() => {
    if (!loading) loadCustomers()
  }, [loading, loadCustomers])

  const resetErpSearch = () => {
    setErpSearchQuery('')
    setErpSearchResults([])
    setShowErpDropdown(false)
    if (erpSearchTimer.current) clearTimeout(erpSearchTimer.current)
  }

  const handleErpSearchChange = (q: string) => {
    setErpSearchQuery(q)
    setShowErpDropdown(false)
    if (erpSearchTimer.current) clearTimeout(erpSearchTimer.current)
    if (!form.erp_id || q.length < 2) {
      setErpSearchResults([])
      return
    }
    erpSearchTimer.current = setTimeout(async () => {
      setErpSearchLoading(true)
      try {
        const results = await api.searchErpCustomers(form.erp_id as number, q)
        setErpSearchResults(results)
        setShowErpDropdown(true)
      } catch {
        setErpSearchResults([])
      } finally {
        setErpSearchLoading(false)
      }
    }, 350)
  }

  const handleErpResultSelect = (result: {
    erp_customer_id: string
    customer_name: string
    city: string | null
  }) => {
    setForm((f) => ({
      ...f,
      erp_customer_id: result.erp_customer_id,
      customer_name: result.customer_name,
      city: result.city ?? f.city,
    }))
    setErpSearchQuery(result.customer_name)
    setShowErpDropdown(false)
    setErpSearchResults([])
  }

  const openCreate = () => {
    setEditingId(null)
    setForm(emptyForm)
    resetErpSearch()
    setShowModal(true)
    setError('')
  }

  const openEdit = (customer: AdminCustomer) => {
    setEditingId(customer.id)
    setForm({
      erp_id: customer.erp_id,
      erp_customer_id: customer.erp_customer_id,
      customer_name: customer.customer_name,
      siren: customer.siren ?? '',
      email: customer.email ?? '',
      phone: customer.phone ?? '',
      address: customer.address ?? '',
      city: customer.city ?? '',
      postal_code: customer.postal_code ?? '',
      is_active: customer.is_active,
    })
    resetErpSearch()
    setShowModal(true)
    setError('')
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      const payload = { ...form, erp_id: form.erp_id === '' ? undefined : form.erp_id }
      if (editingId) {
        await api.updateAdminCustomer(editingId, payload)
      } else {
        await api.createAdminCustomer(payload)
      }
      setShowModal(false)
      await loadCustomers()
    } catch (err: unknown) {
      setError(getApiErrorKey(err, 'common.error'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (customer: AdminCustomer) => {
    if (!confirm(t('admin.customers.deleteConfirm', { name: customer.customer_name }))) return
    try {
      await api.deleteAdminCustomer(customer.id)
      await loadCustomers()
    } catch (err: unknown) {
      alert(t(getApiErrorKey(err, 'common.error')))
    }
  }

  const openSyncModal = () => {
    setSyncErpId(filterErpId || (erps.length === 1 ? erps[0].id : ''))
    setSyncLimit(10)
    setSyncCandidates([])
    setSyncSelected(new Set())
    setSyncResult(null)
    setSyncError('')
    setSyncFilterInvoices(false)
    setSyncFilterInvoicesMonths(6)
    setSyncFilterProposals(false)
    setSyncFilterProposalsMonths(6)
    setShowSyncModal(true)
  }

  const handleSyncPreview = async () => {
    if (!syncErpId) return
    setSyncLoading(true)
    setSyncError('')
    setSyncCandidates([])
    setSyncSelected(new Set())
    setSyncResult(null)
    try {
      const result = await api.syncPreviewCustomers(syncErpId as number, syncLimit, {
        hasInvoices: syncFilterInvoices,
        invoicesMonths: syncFilterInvoicesMonths,
        hasProposals: syncFilterProposals,
        proposalsMonths: syncFilterProposalsMonths,
      })
      setSyncCandidates(result.candidates)
      setSyncTotalInErp(result.total_in_erp ?? 0)
      setSyncAlreadySynced(result.already_synced ?? 0)
      setSyncSelected(new Set(result.candidates.map((c) => c.erp_customer_id)))
    } catch (err: unknown) {
      setSyncError(getApiErrorKey(err, 'common.error'))
    } finally {
      setSyncLoading(false)
    }
  }

  const toggleSyncSelect = (id: string) => {
    setSyncSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleSyncImport = async () => {
    if (!syncErpId || syncSelected.size === 0) return
    setSyncImporting(true)
    setSyncError('')
    try {
      const toImport = syncCandidates.filter((c) => syncSelected.has(c.erp_customer_id))
      const result = await api.syncImportCustomers(syncErpId as number, toImport)
      setSyncResult(result)
      setSyncCandidates([])
      await loadCustomers()
    } catch (err: unknown) {
      setSyncError(getApiErrorKey(err, 'common.error'))
    } finally {
      setSyncImporting(false)
    }
  }

  const handleExportCSV = () => {
    const rows = customers.map((c) => ({
      id: c.id,
      erp_customer_id: c.erp_customer_id,
      customer_name: c.customer_name,
      siren: c.siren ?? '',
      email: c.email ?? '',
      phone: c.phone ?? '',
      address: c.address ?? '',
      city: c.city ?? '',
      postal_code: c.postal_code ?? '',
      is_active: c.is_active ? '1' : '0',
      erp_name: c.erp_name ?? '',
      erp_id: c.erp_id,
    }))
    exportToCSV(rows, `clients_${new Date().toISOString().slice(0, 10)}.csv`)
  }

  const handleImportFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImportFileName(file.name)
    setImportError('')
    setImportResult(null)
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const rows = parseCSV(ev.target?.result as string)
        if (!rows.length) {
          setImportError('Fichier vide ou invalide')
          return
        }
        setImportRows(rows)
      } catch {
        setImportError('Impossible de lire le fichier CSV')
      }
    }
    reader.readAsText(file, 'UTF-8')
  }

  const handleImportSubmit = async () => {
    if (!importErpId || !importRows.length) return
    setImportLoading(true)
    setImportError('')
    setImportResult(null)
    try {
      const result = await api.importCustomersCSV(importErpId as number, importRows)
      setImportResult(result)
      if (!result.errors.length) loadCustomers()
    } catch (err: unknown) {
      setImportError(getApiErrorKey(err, 'common.error'))
    } finally {
      setImportLoading(false)
    }
  }

  const closeImportModal = () => {
    setShowImportModal(false)
    setImportRows([])
    setImportFileName('')
    setImportError('')
    setImportResult(null)
    setImportErpId('')
    if (importFileRef.current) importFileRef.current.value = ''
    if (importResult?.created || importResult?.updated) loadCustomers()
  }

  // Shared input class
  const inputCls =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm'
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1'

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
          {t('admin.customers.title')}
        </h1>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleExportCSV}
            disabled={!customers.length}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline">{t('common.exportCsv')}</span>
          </button>
          <button
            onClick={() => {
              setShowImportModal(true)
              setImportResult(null)
            }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Upload className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline">{t('common.importCsv')}</span>
          </button>
          <button
            onClick={openSyncModal}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline">{t('admin.customers.syncBtn')}</span>
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4 shrink-0" />
            <span>{t('admin.customers.addCustomer')}</span>
          </button>
        </div>
      </div>

      {/* ── Filter ── */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium text-gray-700 shrink-0">
          {t('common.filterByErp')}
        </label>
        <select
          value={filterErpId}
          onChange={(e) => setFilterErpId(e.target.value ? Number(e.target.value) : '')}
          className="flex-1 min-w-[160px] max-w-xs px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">{t('admin.customers.filterAll')}</option>
          {erps.map((erp) => (
            <option key={erp.id} value={erp.id}>
              {erp.erp_name}
            </option>
          ))}
        </select>
      </div>

      {/* ── Table ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Mobile card layout */}
        <ul className="divide-y divide-gray-100 sm:hidden">
          {customers.length === 0 ? (
            <li className="p-6 text-center text-gray-500">{t('admin.customers.none')}</li>
          ) : (
            customers.map((customer) => (
              <li key={customer.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{customer.customer_name}</p>
                    <p className="text-xs text-gray-500 font-mono mt-0.5 truncate">
                      {customer.erp_customer_id}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {customer.erp_name ?? t('common.none')}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs font-medium rounded-full ${customer.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {customer.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 text-xs text-gray-500 truncate">
                    {[customer.email, customer.city].filter(Boolean).join(' · ') ||
                      t('common.none')}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => openEdit(customer)}
                      className="p-1.5 text-gray-400 hover:text-primary-600 rounded"
                      title={t('common.edit')}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(customer)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                      title={t('common.delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>

        {/* Desktop table layout */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.customers.colName')}
                </th>
                <th className="hidden md:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.customers.colSiren')}
                </th>
                <th className="hidden sm:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.customers.colErp')}
                </th>
                <th className="hidden lg:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.customers.colErpId')}
                </th>
                <th className="hidden md:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.customers.colEmail')}
                </th>
                <th className="hidden lg:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.customers.colCity')}
                </th>
                <th className="hidden lg:table-cell text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.customers.colUsers')}
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.customers.colStatus')}
                </th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">
                  {t('admin.customers.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {customers.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500 text-sm">
                    {t('admin.customers.none')}
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900 text-sm max-w-[180px] truncate">
                      {customer.customer_name}
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-500 font-mono">
                      {customer.siren ?? t('common.none')}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3">
                      <span className="inline-block px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                        {customer.erp_name ?? t('common.none')}
                      </span>
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-500 font-mono">
                      {customer.erp_customer_id}
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-500 max-w-[160px] truncate">
                      {customer.email ?? t('common.none')}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-500">
                      {customer.city ?? t('common.none')}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-sm text-gray-500">
                      {customer.user_count ?? 0}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${customer.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
                      >
                        {customer.is_active ? t('common.active') : t('common.inactive')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(customer)}
                          className="p-2 text-gray-400 hover:text-primary-600 transition-colors rounded"
                          title={t('common.edit')}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(customer)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors rounded"
                          title={t('common.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Sync Modal ── */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-gray-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('admin.customers.syncTitle')}
              </h2>
              <button
                onClick={() => setShowSyncModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {syncError && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{t(syncError)}</div>
              )}
              {syncResult && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-800 font-medium text-sm">
                    {t('admin.customers.syncSuccess', {
                      imported: syncResult.imported,
                      skipped: syncResult.skipped,
                    })}
                  </p>
                </div>
              )}

              {/* ERP + limit — stack on mobile */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('admin.customers.syncSelectErp')}</label>
                  <select
                    value={syncErpId}
                    onChange={(e) => {
                      setSyncErpId(e.target.value ? Number(e.target.value) : '')
                      setSyncCandidates([])
                      setSyncResult(null)
                    }}
                    className={inputCls}
                  >
                    <option value="">{t('admin.customers.selectErp')}</option>
                    {erps.map((erp) => (
                      <option key={erp.id} value={erp.id}>
                        {erp.erp_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>{t('admin.customers.syncLimit')}</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={syncLimit}
                    onChange={(e) =>
                      setSyncLimit(Math.max(1, Math.min(100, Number(e.target.value))))
                    }
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Activity filters */}
              <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                <p className="text-sm font-medium text-gray-700">
                  {t('admin.customers.syncFilters')}
                </p>
                {[
                  {
                    checked: syncFilterInvoices,
                    setChecked: setSyncFilterInvoices,
                    months: syncFilterInvoicesMonths,
                    setMonths: setSyncFilterInvoicesMonths,
                    key: 'syncFilterInvoices',
                  },
                  {
                    checked: syncFilterProposals,
                    setChecked: setSyncFilterProposals,
                    months: syncFilterProposalsMonths,
                    setMonths: setSyncFilterProposalsMonths,
                    key: 'syncFilterProposals',
                  },
                ].map((f) => (
                  <div key={f.key} className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={f.checked}
                        onChange={(e) => {
                          f.setChecked(e.target.checked)
                          setSyncCandidates([])
                        }}
                        className="w-4 h-4 text-primary-600 rounded"
                      />
                      <span className="text-sm text-gray-700">{t(`admin.customers.${f.key}`)}</span>
                    </label>
                    {f.checked && (
                      <select
                        value={f.months}
                        onChange={(e) => f.setMonths(Number(e.target.value))}
                        className="text-sm px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        {[1, 3, 6, 12].map((n) => (
                          <option key={n} value={n}>
                            {t('admin.customers.syncFilterMonths', { n })}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={handleSyncPreview}
                disabled={!syncErpId || syncLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
                {syncLoading ? t('common.loading') : t('admin.customers.syncPreview')}
              </button>

              {/* Candidates */}
              {syncCandidates.length > 0 && (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 bg-gray-50 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-700">
                      {t('admin.customers.syncFoundCount', {
                        count: syncTotalInErp,
                        synced: syncAlreadySynced,
                      })}
                    </span>
                    <div className="flex gap-2 text-xs">
                      <button
                        onClick={() =>
                          setSyncSelected(new Set(syncCandidates.map((c) => c.erp_customer_id)))
                        }
                        className="text-primary-600 hover:underline"
                      >
                        {t('admin.customers.syncSelectAll')}
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => setSyncSelected(new Set())}
                        className="text-gray-500 hover:underline"
                      >
                        {t('admin.customers.syncDeselectAll')}
                      </button>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
                    {syncCandidates.map((c) => (
                      <div
                        key={c.erp_customer_id}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleSyncSelect(c.erp_customer_id)}
                      >
                        <button type="button" className="flex-shrink-0 text-primary-600">
                          {syncSelected.has(c.erp_customer_id) ? (
                            <CheckSquare className="w-5 h-5" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {c.customer_name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {c.erp_customer_id}
                            {c.city ? ` · ${c.city}` : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {syncCandidates.length === 0 && !syncLoading && !syncResult && syncError === '' && (
                <p className="text-sm text-gray-400 italic">{t('admin.customers.syncHint')}</p>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-3 p-5 border-t border-gray-200">
              <button
                onClick={() => setShowSyncModal(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.close')}
              </button>
              {syncCandidates.length > 0 && (
                <button
                  onClick={handleSyncImport}
                  disabled={syncImporting || syncSelected.size === 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${syncImporting ? 'animate-spin' : ''}`} />
                  {syncImporting
                    ? t('common.saving')
                    : t('admin.customers.syncImport', { count: syncSelected.size })}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? t('admin.customers.modalEdit') : t('admin.customers.modalCreate')}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{t(error)}</div>
              )}

              {/* ERP selector */}
              <div>
                <label className={labelCls}>{t('admin.customers.fieldErp')}</label>
                <select
                  value={form.erp_id}
                  onChange={(e) => {
                    setForm({ ...form, erp_id: Number(e.target.value) })
                    resetErpSearch()
                  }}
                  className={inputCls}
                >
                  <option value="">{t('admin.customers.selectErp')}</option>
                  {erps.map((erp) => (
                    <option key={erp.id} value={erp.id}>
                      {erp.erp_name} ({erp.erp_type})
                    </option>
                  ))}
                </select>
              </div>

              {/* ERP search autocomplete — create mode only */}
              {!editingId && form.erp_id && (
                <div className="relative">
                  <label className={labelCls}>{t('admin.customers.erpSearch')}</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={erpSearchQuery}
                      onChange={(e) => handleErpSearchChange(e.target.value)}
                      onFocus={() => erpSearchResults.length > 0 && setShowErpDropdown(true)}
                      onBlur={() => setTimeout(() => setShowErpDropdown(false), 150)}
                      placeholder={t('admin.customers.erpSearchPlaceholder')}
                      className="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    {erpSearchLoading && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-primary-500" />
                    )}
                  </div>
                  {showErpDropdown && erpSearchResults.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                      {erpSearchResults.map((r) => (
                        <button
                          key={r.erp_customer_id}
                          type="button"
                          onMouseDown={() => handleErpResultSelect(r)}
                          className="w-full text-left px-4 py-2.5 hover:bg-primary-50 transition-colors border-b border-gray-100 last:border-0"
                        >
                          <p className="text-sm font-medium text-gray-900">{r.customer_name}</p>
                          <p className="text-xs text-gray-500">
                            {r.erp_customer_id}
                            {r.city ? ` · ${r.city}` : ''}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                  {showErpDropdown &&
                    erpSearchResults.length === 0 &&
                    !erpSearchLoading &&
                    erpSearchQuery.length >= 2 && (
                      <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
                        <p className="text-sm text-gray-500 italic">
                          {t('admin.customers.erpSearchNoResult')}
                        </p>
                      </div>
                    )}
                </div>
              )}

              {/* ERP customer ID */}
              <div>
                <label className={labelCls}>{t('admin.customers.fieldErpCustomerId')}</label>
                <input
                  type="text"
                  value={form.erp_customer_id}
                  onChange={(e) => setForm({ ...form, erp_customer_id: e.target.value })}
                  placeholder={
                    (
                      customerIdHelps[erps.find((e) => e.id === form.erp_id)?.erp_type ?? ''] ??
                      defaultHelp
                    ).placeholder
                  }
                  className={inputCls}
                />
                {form.erp_id && (
                  <p className="mt-1.5 text-xs text-gray-500 flex items-start gap-1">
                    <HelpCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-500" />
                    <span>
                      {
                        (
                          customerIdHelps[erps.find((e) => e.id === form.erp_id)?.erp_type ?? ''] ??
                          defaultHelp
                        ).help
                      }
                    </span>
                  </p>
                )}
              </div>

              {/* Name */}
              <div>
                <label className={labelCls}>{t('admin.customers.fieldName')}</label>
                <input
                  type="text"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  className={inputCls}
                />
              </div>

              {/* SIREN */}
              <div>
                <label className={labelCls}>{t('admin.customers.fieldSiren')}</label>
                <input
                  type="text"
                  value={form.siren}
                  onChange={(e) => setForm({ ...form, siren: e.target.value })}
                  placeholder="ex: 123 456 789"
                  maxLength={20}
                  className={inputCls}
                />
              </div>

              {/* Email */}
              <div>
                <label className={labelCls}>{t('admin.customers.fieldEmail')}</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                />
              </div>

              {/* Phone — full width so the flag+dialcode+input don't overflow */}
              <PhoneInput
                value={form.phone}
                onChange={(phone) => setForm({ ...form, phone })}
                label={t('admin.customers.fieldPhone')}
                labelCls={labelCls}
                fieldCls="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />

              {/* Address */}
              <div>
                <label className={labelCls}>{t('admin.customers.fieldAddress')}</label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className={inputCls}
                />
              </div>

              {/* City + Postal code — side by side on sm+, stacked on mobile */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>{t('admin.customers.fieldCity')}</label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>{t('admin.customers.fieldPostalCode')}</label>
                  <input
                    type="text"
                    value={form.postal_code}
                    onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  {t('admin.customers.fieldActive')}
                </label>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3 p-5 border-t border-gray-200">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.customer_name || !form.erp_id || !form.erp_customer_id}
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CSV Import Modal ── */}
      {showImportModal && (
        <div className="fixed inset-0 bg-gray-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('admin.customers.importCsvTitle')}
              </h2>
              <button onClick={closeImportModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {erps.length > 1 && (
                <div>
                  <label className={labelCls}>{t('admin.customers.colErp')}</label>
                  <select
                    value={importErpId}
                    onChange={(e) => setImportErpId(e.target.value ? Number(e.target.value) : '')}
                    className={inputCls}
                  >
                    <option value="">{t('common.select')}</option>
                    {erps.map((erp) => (
                      <option key={erp.id} value={erp.id}>
                        {erp.erp_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
                <p className="font-semibold mb-1">{t('admin.customers.importCsvFormat')}</p>
                <code className="font-mono break-all">
                  erp_customer_id,customer_name,siren,email,phone,address,city,postal_code,is_active
                </code>
                <p className="mt-1 text-blue-700">{t('admin.customers.importCsvHint')}</p>
              </div>

              <div>
                <label className={labelCls}>{t('admin.customers.importCsvFile')}</label>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={handleImportFileChange}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                {importFileName && (
                  <p className="mt-1 text-xs text-gray-500">
                    {importFileName} — {importRows.length} {t('admin.customers.importCsvLines')}
                  </p>
                )}
              </div>

              {importError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-red-800">{t(importError)}</p>
                </div>
              )}

              {importResult && (
                <div className="space-y-2">
                  <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                    <p className="text-sm text-green-800">
                      {t('admin.customers.importCsvResult', {
                        created: importResult.created,
                        updated: importResult.updated,
                      })}
                    </p>
                  </div>
                  {importResult.errors.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      <p className="font-semibold mb-1">
                        {t('admin.customers.importCsvErrors', {
                          count: importResult.errors.length,
                        })}
                      </p>
                      <ul className="space-y-0.5">
                        {importResult.errors.slice(0, 5).map((e, i) => (
                          <li key={i} className="text-xs font-mono">
                            Ligne {e.line}: {e.message}
                          </li>
                        ))}
                        {importResult.errors.length > 5 && (
                          <li className="text-xs text-amber-600">
                            …+{importResult.errors.length - 5} autres
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-3 p-5 border-t border-gray-200">
              <button
                onClick={closeImportModal}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.close')}
              </button>
              {!importResult && (
                <button
                  onClick={handleImportSubmit}
                  disabled={
                    importLoading || !importRows.length || (erps.length > 1 && !importErpId)
                  }
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  <Upload className={`w-4 h-4 ${importLoading ? 'animate-spin' : ''}`} />
                  {importLoading
                    ? t('common.saving')
                    : t('admin.customers.importCsvSubmit', { count: importRows.length })}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
